import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Autocomplete,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PressDiagram from './PressDiagram';
import {
  MESH_COUNTS,
  FLASH_HEAD,
  ROLLER_HEAD,
  applyDefaultHeadLayout,
  syncPressSetupWithLine,
  getActiveLocations,
  getBlockedHeads,
  isUnderbaseColor,
  isDuplicateUnderbaseEntry,
  resolveInkDisplayHex,
  resolveMeshCount
} from '../utils/pressSetup';

export const SharpCheckbox = styled('input')`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid var(--al-primary);
  background: transparent;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;

  &:checked {
    background: var(--al-primary);

    &:after {
      content: '×';
      color: var(--al-bg);
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      font-size: 14px;
      font-weight: bold;
      line-height: 1;
    }
  }

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: none;
    border-color: var(--al-primary);
  }
`;

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#363636',
    color: 'white',
    borderRadius: 0,
    fontSize: '0.8rem',
    height: '36px',
    textTransform: 'uppercase',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(var(--al-primary-rgb), 0.5)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--al-primary)' }
  },
  '& .MuiSelect-select': { padding: '8px 12px', textTransform: 'uppercase' }
};

const InkAutocompleteField = styled(TextField)(({ needsreview }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: needsreview === 'true' ? 'rgba(255, 193, 7, 0.1)' : '#363636',
    color: 'white',
    borderRadius: 0,
    '& fieldset': {
      borderColor: needsreview === 'true' ? '#FFC107' : 'rgba(255, 255, 255, 0.2)',
      borderWidth: needsreview === 'true' ? '2px' : '1px'
    },
    '&:hover fieldset': {
      borderColor: needsreview === 'true' ? '#FFC107' : 'rgba(var(--al-primary-rgb), 0.5)'
    },
    '&.Mui-focused fieldset': {
      borderColor: needsreview === 'true' ? '#FFC107' : 'var(--al-primary)'
    }
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.7rem',
    textTransform: 'uppercase'
  },
  '& .MuiInputBase-input': {
    color: 'white',
    textTransform: 'uppercase',
    fontSize: '0.8rem'
  }
}));

const locationLabel = (loc) => {
  if (loc === 'front') return 'Front';
  if (loc === 'back') return 'Back';
  return 'Other';
};

const normalizePressSetup = (draft) => {
  const normalized = {};
  for (const loc of Object.keys(draft || {})) {
    const setup = draft[loc];
    if (!setup) continue;
    normalized[loc] = {
      ...setup,
      screens: (setup.screens || []).map((screen) => ({
        ...screen,
        meshCount: resolveMeshCount(screen)
      }))
    };
  }
  return normalized;
};

const PressSetupPanel = ({
  lineData,
  pressSetup,
  inkCatalog = [],
  editableInks = false,
  onInkColorChange,
  needsInkReview,
  onPressSetupChange,
  onSave,
  compact = false,
  showPressNotes = true,
  showSaveButton = false,
  saveLabel = 'Save Press Setup',
  isSaving = false
}) => {
  const activeLocations = useMemo(() => getActiveLocations(lineData), [lineData]);
  const [draft, setDraft] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const lineId = lineData?.id ?? lineData?.docId ?? null;

  useEffect(() => {
    const synced = syncPressSetupWithLine(pressSetup, lineData);
    const next = synced || {};
    setDraft(next);
    setTabIndex(0);
    onPressSetupChange?.(normalizePressSetup(next));
  }, [lineId, pressSetup, onPressSetupChange]);

  const currentLoc = activeLocations[tabIndex] || activeLocations[0];
  const currentSetup = currentLoc ? draft?.[currentLoc] : null;

  const updateLocation = useCallback((loc, updater) => {
    setDraft((prev) => {
      const locData = prev?.[loc];
      const nextLoc = typeof updater === 'function' ? updater(locData) : updater;
      const next = { ...prev, [loc]: nextLoc };
      onPressSetupChange?.(normalizePressSetup(next));
      return next;
    });
  }, [onPressSetupChange]);

  const handleApplyDefaults = () => {
    if (!currentLoc || !currentSetup) return;
    updateLocation(currentLoc, (locData) => applyDefaultHeadLayout(locData, inkCatalog));
  };

  const handleSave = () => {
    if (!draft || !onSave) return;

    let next = { ...draft };
    for (const loc of activeLocations) {
      const setup = next[loc];
      if (!setup) continue;
      const colors = (setup.screens || []).map((s) => s.color);
      if (colors.some((_, i) => isDuplicateUnderbaseEntry(colors, i))) {
        next[loc] = applyDefaultHeadLayout(setup, inkCatalog);
      }
    }

    onSave(normalizePressSetup(next));
  };

  const headOptions = (locData, screen) => {
    const blocked = getBlockedHeads(
      locData.flashEnabled === true,
      locData.rollerEnabled === true
    );
    return [1, 2, 3, 4, 5, 6, 7, 8].map((h) => ({
      value: h,
      disabled: blocked.has(h) && screen.head !== h
    }));
  };

  const getInkOptions = (colorValue) => {
    const q = String(colorValue || '').trim().toLowerCase();
    if (!q) return inkCatalog.map((ink) => ink.name).filter(Boolean);
    return inkCatalog
      .filter((ink) => ink.name?.toLowerCase().includes(q))
      .map((ink) => ink.name);
  };

  if (activeLocations.length === 0) {
    return (
      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', textTransform: 'uppercase' }}>
        Add ink colors to a print location before configuring press setup.
      </Typography>
    );
  }

  const diagramSize = compact ? 'compact' : 'default';
  const diagramWidth = compact ? 268 : 336;

  return (
    <Box sx={{ textTransform: 'uppercase', fontFamily: "'Montserrat', sans-serif" }}>
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        sx={{
          mb: 2,
          minHeight: 36,
          '& .MuiTab-root': {
            color: 'rgba(255,255,255,0.5)',
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            minHeight: 36,
            py: 0.5
          },
          '& .Mui-selected': { color: 'var(--al-primary)' },
          '& .MuiTabs-indicator': { backgroundColor: 'var(--al-primary)' }
        }}
      >
        {activeLocations.map((loc) => (
          <Tab key={loc} label={locationLabel(loc)} />
        ))}
      </Tabs>

      {currentSetup && (
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                size="small"
                onClick={handleApplyDefaults}
                sx={{
                  color: 'var(--al-primary)',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  borderRadius: 0,
                  border: '1px solid rgba(var(--al-primary-rgb), 0.4)',
                  px: 1.5,
                  py: 0.75,
                  '&:hover': { backgroundColor: 'rgba(var(--al-primary-rgb), 0.1)' }
                }}
              >
                Apply Default Layout
              </Button>
              <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
                <SharpCheckbox
                  type="checkbox"
                  checked={currentSetup.flashEnabled === true}
                  onChange={(e) =>
                    updateLocation(currentLoc, (d) => ({ ...d, flashEnabled: e.target.checked }))
                  }
                />
                <Typography sx={{ fontSize: '0.75rem', color: 'white' }}>
                  Flash (Head {FLASH_HEAD})
                </Typography>
              </Box>
              <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
                <SharpCheckbox
                  type="checkbox"
                  checked={currentSetup.rollerEnabled === true}
                  onChange={(e) =>
                    updateLocation(currentLoc, (d) => ({ ...d, rollerEnabled: e.target.checked }))
                  }
                />
                <Typography sx={{ fontSize: '0.75rem', color: 'white' }}>
                  Roller (Head {ROLLER_HEAD})
                </Typography>
              </Box>
              {showSaveButton && (
                <Button
                  size="small"
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={{
                    ml: 'auto',
                    backgroundColor: 'var(--al-primary)',
                    color: 'var(--al-bg)',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    borderRadius: 0,
                    px: 2,
                    py: 0.75,
                    '&:hover': { backgroundColor: '#7FE87F' },
                    '&:disabled': { backgroundColor: 'rgba(var(--al-primary-rgb), 0.3)' }
                  }}
                >
                  {isSaving ? 'Saving...' : saveLabel}
                </Button>
              )}
            </Box>

            <Typography
              sx={{
                color: 'white',
                mb: 1.5,
                fontWeight: 700,
                fontSize: '0.85rem',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                pb: 0.75
              }}
            >
              {locationLabel(currentLoc)} ({(currentSetup.screens || []).length}{' '}
              {(currentSetup.screens || []).length === 1 ? 'Color' : 'Colors'})
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {(currentSetup.screens || []).map((screen, index) => {
                const inkKey = `${currentLoc}InkColors`;
                const locationInkColors = editableInks
                  ? (lineData?.[inkKey] || [])
                  : (currentSetup.screens || []).map((s) => s.color);
                const editableColor = editableInks
                  ? (lineData?.[inkKey]?.[index] ?? screen.color ?? '')
                  : (screen.color || '');
                const isDupUnderbase = isDuplicateUnderbaseEntry(locationInkColors, index);
                const showAsUnderbase = isUnderbaseColor(editableColor) || isDupUnderbase;
                const needsReview = editableInks && needsInkReview?.(editableColor);
                const swatchHex = resolveInkDisplayHex(editableColor, inkCatalog);

                return (
                  <Box
                    key={`${currentLoc}-screen-${index}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: editableInks
                        ? 'minmax(0, 1fr) 76px 88px'
                        : 'minmax(0, 1fr) 88px 100px',
                      gap: 1.25,
                      alignItems: 'start',
                      backgroundColor: '#363636',
                      p: 1.25,
                      border: '1px solid rgba(255,255,255,0.08)'
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            mt: 1,
                            borderRadius: '2px',
                            flexShrink: 0,
                            backgroundColor: swatchHex || 'rgba(255,255,255,0.15)',
                            border: '1px solid rgba(255,255,255,0.25)'
                          }}
                        />
                        {editableInks ? (
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Autocomplete
                              freeSolo
                              size="small"
                              options={getInkOptions(editableColor)}
                              value={editableColor}
                              onChange={(_, newValue) => {
                                onInkColorChange?.(currentLoc, index, newValue || '', true);
                              }}
                              onInputChange={(_, newInputValue, reason) => {
                                if (reason === 'input') {
                                  onInkColorChange?.(currentLoc, index, newInputValue || '', false);
                                }
                              }}
                              clearOnBlur={false}
                              onBlur={(e) => {
                                onInkColorChange?.(currentLoc, index, e.target.value || '', true);
                              }}
                              renderInput={(params) => (
                                <InkAutocompleteField
                                  {...params}
                                  label={`${locationLabel(currentLoc)} ${index + 1}`}
                                  needsreview={needsReview ? 'true' : 'false'}
                                  placeholder="Enter ink color"
                                />
                              )}
                              renderOption={(props, option) => {
                                const ink = inkCatalog.find((i) => i.name === option);
                                return (
                                  <Box
                                    component="li"
                                    {...props}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                                  >
                                    {ink?.hexCode && (
                                      <Box
                                        sx={{
                                          width: 16,
                                          height: 16,
                                          borderRadius: '2px',
                                          backgroundColor: ink.hexCode,
                                          border: '1px solid rgba(255,255,255,0.2)'
                                        }}
                                      />
                                    )}
                                    <Typography sx={{ textTransform: 'uppercase', fontSize: '0.8rem' }}>
                                      {option}
                                    </Typography>
                                  </Box>
                                );
                              }}
                            />
                            {needsReview && (
                              <Chip
                                label="Not in database"
                                size="small"
                                sx={{
                                  mt: 0.5,
                                  backgroundColor: '#FFC107',
                                  color: '#000',
                                  fontSize: '0.6rem',
                                  height: '18px',
                                  fontWeight: 600,
                                  borderRadius: 0
                                }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              fontWeight: showAsUnderbase ? 700 : 500,
                              color: showAsUnderbase ? 'var(--al-primary)' : 'white',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              pt: 0.75
                            }}
                          >
                            {screen.color}
                            {isDupUnderbase && (
                              <Typography
                                component="span"
                                sx={{ fontSize: '0.65rem', color: 'rgba(var(--al-primary-rgb), 0.75)', ml: 0.75 }}
                              >
                                (UB)
                              </Typography>
                            )}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <FormControl size="small" sx={fieldSx}>
                      <Select
                        value={resolveMeshCount(screen)}
                        onChange={(e) =>
                          updateLocation(currentLoc, (d) => ({
                            ...d,
                            screens: d.screens.map((s, i) =>
                              i === index ? { ...s, meshCount: Number(e.target.value) } : s
                            )
                          }))
                        }
                      >
                        {MESH_COUNTS.map((m) => (
                          <MenuItem key={m} value={m}>
                            {m}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ ...fieldSx, minWidth: 88 }}>
                      <Select
                        value={screen.head ?? ''}
                        displayEmpty
                        renderValue={(value) =>
                          value !== '' && value != null ? String(value) : 'HEAD'
                        }
                        onChange={(e) =>
                          updateLocation(currentLoc, (d) => ({
                            ...d,
                            screens: d.screens.map((s, i) =>
                              i === index ? { ...s, head: Number(e.target.value) } : s
                            )
                          }))
                        }
                      >
                        <MenuItem value="" disabled>
                          HEAD
                        </MenuItem>
                        {headOptions(currentSetup, screen).map(({ value, disabled }) => (
                          <MenuItem key={value} value={value} disabled={disabled}>
                            {value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                );
              })}
            </Box>

            {showPressNotes && (
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Press Notes"
                value={currentSetup.notes || ''}
                onChange={(e) =>
                  updateLocation(currentLoc, (d) => ({ ...d, notes: e.target.value }))
                }
                inputProps={{ style: { textTransform: 'uppercase' } }}
                InputLabelProps={{ sx: { textTransform: 'uppercase' } }}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#363636',
                    color: 'white',
                    borderRadius: 0,
                    fontSize: '0.8rem',
                    textTransform: 'uppercase',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(var(--al-primary-rgb), 0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'var(--al-primary)' }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    '&.Mui-focused': { color: 'var(--al-primary)' }
                  }
                }}
              />
            )}
          </Box>

          <Box sx={{ width: { xs: '100%', md: diagramWidth }, flexShrink: 0, alignSelf: 'flex-start' }}>
            <PressDiagram
              locationSetup={currentSetup}
              inkCatalog={inkCatalog}
              size={diagramSize}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PressSetupPanel;
